<div
    class="dashboard-container container-fluid px-0 d-flex flex-column min-vh-100"
    :class="{ 'tablet-fixed-shell': is_tablet_shell }">
    <BolideLoader
        v-if="is_loading"
        class="dashboard-startup-loader position-fixed top-0 start-0 w-100 h-100 bg-white"
        :redirect_on_complete="false"
    />

    <div class="row g-0 vh-100 overflow-hidden">

        <!-- Desktop sidebar -->
        <div v-if="!is_mobile"
            class="col-auto sidebar-wrapper sidebar-offset d-flex flex-column flex-shrink-0 position-sticky top-0 vh-100 overflow-hidden overflow-x-visible"
            :class="{ 'sidebar-collapsed': sidebar_collapsed }">
            <!-- DESKTOP SIDEBAR SCROLL AREA -->
            <div class="h-100 overflow-auto overflow-x-hidden no-scrollbar">
                <!-- Portfolio mode -->
                <PortfolioSidebar v-if="!is_property_view && can_view_portfolio" :tiles="tiles"
                    :current_active_property="selected_property?.title" :active_item="active_menu_item"
                    :sidebar_collapsed="sidebar_collapsed"
                    @tile-clicked="handleTileClick" @global-menu-selected="handleGlobalMenuSelected"
                    @property-menu-selected="handlePropertyMenuSelected" @global-selected="goBack" />

                <!-- Property mode -->
                <PropertySidebar v-else-if="selected_property" :active_property="selected_property" :properties="tiles"
                    :active_item="active_menu_item" :can_view_portfolio="can_view_portfolio"
                    :sidebar_collapsed="sidebar_collapsed"
                    @switch-property="handleTileClick" @go-back="goBack"
                    @property-menu-selected="handlePropertyMenuSelected" />
            </div>
        </div>

        <!-- Mobile sidebar overlay -->
        <div v-if="is_mobile && mobile_sidebar_open" class="mobile-sidebar-overlay position-fixed d-flex align-items-stretch top-0 bottom-0 start-0 end-0" @click.self="close_sidebar">
            <div class="mobile-sidebar-panel h-100 overflow-hidden text-dark">
                <div class="h-100 overflow-auto overflow-x-hidden">
                    <!-- Portfolio mode -->
                    <PortfolioSidebar v-if="!is_property_view && can_view_portfolio" :tiles="tiles"
                        :current_active_property="selected_property?.title" :active_item="active_menu_item"
                        @tile-clicked="handleTileClick" @global-menu-selected="handleGlobalMenuSelected"
                        @property-menu-selected="handlePropertyMenuSelected" @global-selected="goBack"
                        @close-sidebar="close_sidebar">
                        <template #sidebar-header-actions>
                            <button class="sidebar-close-btn btn btn-sm bg-white border text-dark" @click="close_sidebar" aria-label="Close sidebar">
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </template>
                    </PortfolioSidebar>

                    <!-- Property mode -->
                    <PropertySidebar v-else-if="selected_property" :active_property="selected_property"
                        :properties="tiles" :active_item="active_menu_item"
                        :can_view_portfolio="can_view_portfolio" @switch-property="handleTileClick"
                        @go-back="goBack" @property-menu-selected="handlePropertyMenuSelected"
                        @close-sidebar="close_sidebar">
                        <template #sidebar-header-actions>
                            <button class="sidebar-close-btn btn btn-sm bg-white border text-dark" @click="close_sidebar" aria-label="Close sidebar">
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </template>
                    </PropertySidebar>
                </div>
            </div>
        </div>

        <!-- Sidebar collapse button -->
        <button v-if="!is_mobile && !is_loading" class="sidebar-collapse-btn btn btn-sm border bg-white text-dark float-start translate-middle-x" @click="toggleSidebar">
            <i :class="sidebar_collapsed ? 'bi bi-chevron-double-right' : 'bi bi-chevron-double-left'"></i>
        </button>

        <!-- Main content -->
        <div class="col vh-100 d-flex flex-column overflow-hidden no-scrollbar">
            <!-- HEADER -->
            <HeaderSection :breadcrumb="breadcrumb" @breadcrumb-click="handleBreadcrumbClick"
                @open-assistant="showAssistant = true" @toggle-mobile-sidebar="toggle_mobile_sidebar" />

            <BolideAssistant
                :is_open="showAssistant"
                :scope="assistant_scope"
                :compare_property_options="comparison_property_options"
                :compare_property_ids="selected_compare_property_ids"
                @update:compare_property_ids="selected_compare_property_ids = $event"
                @close-assistant="showAssistant = false"
            />

            <!-- MAIN SCROLL AREA -->
            <main class="dashboard-main flex-grow-1 overflow-auto overflow-x-hidden"
                :class="{ 'dashboard-main-flush': is_property_view }">
                <!-- Global dashboard -->
                <div v-if="currentView === 'main'" class="dashboard-main-view h-100">
                    <div class="row g-0 h-100">
                        <PortfolioSuperSetChart :content_class="getMainContentClass()" chart_id="dashboard-main"
                            :iframe_loading="iframeLoading" :iframe_error="iframeError" />
                    </div>
                </div>

                <div v-else-if="currentView === 'no-assigned-properties'" class="d-flex align-items-center justify-content-center h-100 p-4">
                    <div class="card text-center p-4 text-dark border rounded-3 shadow">
                        <h5 class="text-dark fw-bold mb-2">No shafts assigned</h5>
                        <p class="text-secondary text-xs mb-0">
                            Ask an administrator to assign shafts to this user.
                        </p>
                    </div>
                </div>

                <!-- Property dashboard -->
                <div v-else-if="is_property_view && !isDetailView(currentView)"
                    class="dashboard-main-view h-100">
                    <div class="row g-0 h-100">
                        <PortfolioSuperSetChart :content_class="getMainContentClass()" :chart_id="'dashboard-' + currentView"
                            :iframe_loading="iframeLoading" :iframe_error="iframeError" />
                    </div>
                </div>

                <!-- Detail dashboard -->
                <div v-else-if="isDetailView(currentView)" class="dashboard-detail-view">
                    <div class="card rounded-3 bg-transparent border mt-4 mb-4">
                        <div class="portfolio-chart">
                            <div class="superset-embedded-container m-0 p-0 position-relative">
                                <div :id="'dashboard-' + currentView"></div>

                                <div v-if="iframeLoading" class="iframe-loading position-absolute d-flex align-items-center justify-content-center gap-2 fw-bold top-0 bottom-0 start-0 end-0 text-sm text-dark">
                                    <div class="spinner-border spinner-border-sm me-2"></div>
                                    Loading embedded dashboard...

                                    <div v-if="iframeError" class="error-message mt-2 text-danger">
                                        Failed to load dashboard. Please try refreshing.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    </div>
</div>
